import {
  VisualTrackWrapper,
  TimeLayerRow,
  AudioTrackWrapper,
} from '../api/model';
import { autocorrectAudioTracks, autocorrectVisualTracks } from '../api/tapi';
import _ from 'lodash-extended';
import app from 'app';
import fg from '#/libs/FrameGrid';

export default {
  setVisualLayerDuration(state, visualDuration) {
    state.trackLayer.visualDuration =
      visualDuration ?? state.trackLayer.visualDuration;
  },

  setAudioLayerDuration(state, audioDuration) {
    state.trackLayer.audioDuration =
      audioDuration ?? state.trackLayer.audioDuration;
  },

  setTimeLayerRowDuration(state, rowDuration) {
    state.timeLayer.rowDuration = rowDuration ?? state.timeLayer.rowDuration;
  },

  setTimeLayerRows(state, { numberOfRows }) {
    if (!state.viewOffsets.axis) {
      app.doctorController.report('setTimeLayerRows called before setAxis?');
      return;
    }

    const rows = new Array(numberOfRows);
    let time = 0;
    const x = state.cssVariables.pxAxisSidePadding;
    let y = state.cssVariables.pxAxisTopPadding;

    const width =
      state.viewOffsets.axis.width - 2 * state.cssVariables.pxAxisSidePadding;

    for (let i = 0, I = numberOfRows; i < I; i++) {
      const row = new TimeLayerRow({
        gridStartTime: time,
        gridDuration: state.timeLayer.rowDuration,
        gridEndTime: time + state.timeLayer.rowDuration,
      });

      row.setRectangle(x, y, width, state.cssVariables.pxRowHeight);
      rows[i] = row;
      y += state.cssVariables.pxRowHeight;
      time += state.timeLayer.rowDuration;
    }

    state.timeLayer.rows = rows;
  },

  appendTimeLayerRow(state) {
    const previous = _.last(state.timeLayer.rows);
    const row = new TimeLayerRow({
      gridStartTime: previous.gridEndTime,
      gridDuration: state.timeLayer.rowDuration,
      gridEndTime: previous.gridEndTime + state.timeLayer.rowDuration,
    });
    const x = previous.rectangle.p1.x;
    const y = previous.rectangle.p2.y;
    const width = previous.rectangle.width;
    row.setRectangle(x, y, width, state.cssVariables.pxRowHeight);
    state.timeLayer.rows.push(row);
  },

  popTimeLayerRow(state) {
    state.timeLayer.rows.pop();
  },

  initTrackLayerVisual(state, { timelineData }) {
    autocorrectVisualTracks(timelineData);

    const arr = [];
    _.eachIn(timelineData, 'visualTracks', (asset) => {
      // establish two directional discoverability by guid
      asset.guid = asset.guid ?? app.guid(asset.id);
      arr.push(new VisualTrackWrapper({ asset }));
    });

    arr.sort((a, b) => a.data.displayStartTime - b.data.displayStartTime);
    state.trackLayer.visual = arr;
  },

  initTrackLayerAudio(state, { lightboxAudio, audioTracks }) {
    const arr = [];

    autocorrectAudioTracks(audioTracks);

    _.eachIn(lightboxAudio, 'audioSegmentContent', (selected) => {
      const placed = _.filter(audioTracks, {
        assetUUID: selected.asset.metadata.assetUUID,
      });
      _.each(placed, (audioOnTrack) => {
        if (audioOnTrack) {
          arr.push(new AudioTrackWrapper({ selected, audioOnTrack }));
        }
      });
    });

    arr.sort((a, b) => a.data.displayStartTime - b.data.displayStartTime);
    state.trackLayer.audio = arr;
  },

  calculateCssVariables(state, { hasTranscriptTrack, hasAudioTrack }) {
    let rowHeight =
      state.cssVariables.pxRowBlankTop +
      state.cssVariables.pxAssetHeight +
      state.cssVariables.pxRowBlankBottom;

    if (hasTranscriptTrack) {
      rowHeight += state.cssVariables.pxTranscriptTrackH;
    }
    if (hasAudioTrack) {
      rowHeight += state.cssVariables.pxAudioTrackH;
    }

    state.cssVariables.pxRowHeight = rowHeight;

    state.cssVariables.pxOverlayH =
      state.cssVariables.pxAssetHeight -
      2 * state.cssVariables.pxVisualSpanBorder;

    state.cssVariables.pxOverlayTopOffset =
      state.cssVariables.pxRowBlankTop + state.cssVariables.pxVisualSpanBorder;

    state.cssVariables.pxAudioTopOffset =
      state.cssVariables.pxRowBlankTop +
      state.cssVariables.pxAssetHeight +
      state.cssVariables.pxTranscriptTrackH;
  },

  setAxis(state, axis) {
    state.viewOffsets.axis = axis;
  },

  setScrollTop(state, scrollTop) {
    state.viewOffsets.scrollTop = scrollTop;
  },

  setSecondhand(state, { time, makeItVisible = false }) {
    state.secondhand = time;
    if (makeItVisible) {
      state.secondhandVisibilityTrigger = Date.now();
    }
  },

  setMODE(state, { name, value }) {
    state.MODE[name] = value;
  },

  setSTATE(state, { name, value }) {
    _.each(state.STATE, (v, k) => {
      if (k === name) {
        state.STATE[name] = value;
      } else {
        // set other states to false
        state.STATE[k] = false;
      }
    });
  },

  setUSE(state, { name, value }) {
    state.USE[name] = value;
  },

  setDndEscort(state, { name, value }) {
    state.dndEscort[name] = value;
  },

  setSceneEscort(state, { name, value }) {
    state.sceneEscort[name] = value;
  },

  setVisualSelected(state, track) {
    state.selected.visual = track;
  },

  setAudioSelected(state, track) {
    state.selected.audio = track;
  },

  setVisualOverlaySelected(state, overlay) {
    state.selected.overlay = overlay;
  },

  setTrackDuration(state, { data, start, duration, end }) {
    data.displayStartTime = fg.trunc(start);
    data.displayDuration = duration;
    data.displayEndTime = fg.trunc(end);
  },

  removeVisualTrack(state, { atIndex }) {
    state.trackLayer.visual.splice(atIndex, 1);
  },

  removeAudioTrack(state, { atIndex }) {
    state.trackLayer.audio.splice(atIndex, 1);
  },

  insertVisualTrack(state, { atIndex, track }) {
    state.trackLayer.visual.splice(atIndex, 0, track);
  },

  insertAudioTrack(state, track) {
    state.trackLayer.audio.push(track);
    state.trackLayer.audio.sort(
      (a, b) => a.data.displayStartTime - b.data.displayStartTime
    );
  },

  replaceVisualTrack(state, { atIndex, newTrack }) {
    state.trackLayer.visual.splice(atIndex, 1, newTrack);
  },

  setVisualRtpState(state, { track, status, percent }) {
    track.rtpState.show = 'DONE' !== status;
    track.rtpState.status = status;
    track.rtpState.percent = percent;
  },

  setVisualTrackingState(state, { track, pending }) {
    track.trackingState.show = pending;
  },

  setVisualRtpByStatus(
    state,
    { track, status, realTimePreviewChecksum, realTimePreviewResultData }
  ) {
    if ('DONE' === status) {
      track.data.realTimePreviewChecksum = realTimePreviewChecksum;
      track.data.realTimePreviewResultData = realTimePreviewResultData;
      track.data._realTimePreviewInProgress = false;
      track.data._tmpRealTimePreviewChecksum = null;
    } else {
      track.data._realTimePreviewInProgress = true;
    }
  },
};
